import { QueryBody } from "@api/internal/utils/types";

/**
 * Generates the payload for listing API requests based on the page type.
 * @param urlPath - The URL path of the listing page.
 * @param page - The current page number (for section pages).
 * @param size - The number of articles to fetch.
 * @param sections - The sections to filter articles by.
 * @param pageType - The type of the page (e.g., "keyword" or "section").
 * @param offset - The offset for pagination (for keyword pages).
 * @returns The payload object for the listing API request.
 */
export const getListingPayload = ({
  urlPath,
  page,
  size,
  sections,
  pageType,
  offset,
  excludeIds,
}: QueryBody): QueryBody => {
  const defaultPayload: QueryBody = {
    size,
    sections,
    excludeIds,
  };

  switch (pageType) {
    case "keyword":
    case "storyThread":
    case "author":
      return { ...defaultPayload, urlPath, offset };

    case "section":
    default:
      return { ...defaultPayload, page };
  }
};

/**
 * Determines the appropriate listing API endpoint based on the page type.
 * @param pageType - The type of the page (e.g., "keyword" or "section").
 * @returns The API endpoint URL as a string.
 */
export const getListingEndpoint = (pageType?: string): string => {
  switch (pageType) {
    case "keyword":
    case "storyThread":
      return "/_plat/api/v1/articles/tags";

    case "author":
      return "/_plat/api/v1/articles/authors";

    case "section":
    default:
      return "/_plat/api/v1/articles/sections";
  }
};

/**
 * Helper function to get the page number for the listing.
 * @param initialList - The initial list of articles.
 * @param loadMoreDataCount - The number of articles to load more. Used to determine if we are on page 2, no value means it is the page 2.
 * @param size - The number of articles to load.
 * @returns {number} The page number.
 */
export const getPage = (
  initialList: number,
  loadMoreDataCount: number,
  size: number
): number => {
  if (loadMoreDataCount === 0) return 2;

  return Math.ceil((initialList + loadMoreDataCount) / size) + 1;
};

/**
 * Helper function to get the offset for the listing.
 * @param page - The page number.
 * @param initialList - The initial list of articles.
 * @param size - The number of articles to load.
 * @returns {number} The offset.
 */
export const getOffset = (
  page: number,
  initialList: number,
  size: number
): number => {
  if (page === 1) return 0;

  return initialList + (page - 2) * size;
};
